import React, { useState, useEffect, useCallback, useRef } from 'react';
// @ts-ignore
import { Radio, Users, Package, Tag, Activity, Pause, Play, RefreshCw, CheckCircle } from 'lucide-react';
import { operationalDashboardService } from '../../services/operationalDashboard';
import type { RealtimeData } from '../../services/operationalDashboard';

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    pendente: 'Pendente',
    pre_carregamento: 'Pré-Carregamento',
    carregado: 'Carregado',
    descarregado: 'Descarregado',
    cancelado: 'Cancelado'
  };
  return map[status] || status;
}

function statusColor(status: string): string {
  const map: Record<string, string> = {
    pendente: 'yellow',
    pre_carregamento: 'blue',
    carregado: 'green',
    descarregado: 'cyan',
    cancelado: 'gray'
  };
  return map[status] || 'gray';
}

const OperationalDashboardRealtime: React.FC = () => {
  const [data, setData] = useState<RealtimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setError('');
      const res = await operationalDashboardService.getRealtime();
      setData(res.data);
      setLastUpdate(new Date());
    } catch (err: unknown) {
      setError('Erro ao carregar monitoramento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(fetchData, 15000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paused, fetchData]);

  const togglePause = () => setPaused(p => !p);

  if (loading) {
    return <div className="op-dashboard-loading"><div className="op-dashboard-spinner" />Carregando tempo real...</div>;
  }

  if (error) {
    return <div className="op-dashboard-error">{error}</div>;
  }

  if (!data) return null;

  const paletes = data.paletes_em_andamento || [];
  const conferentes = data.conferentes_ativos || [];
  const resumo = data.resumo_dia || { paletes_hoje: 0, concluidos_hoje: 0, em_andamento_hoje: 0, etiquetas_hoje: 0 };

  return (
    <>
      <div className="op-dashboard-realtime-header">
        <h2 className="op-dashboard-section-title">
          <Radio size={18} className={paused ? '' : 'op-dashboard-pulse'} /> Operação em Tempo Real
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastUpdate && (
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              Atualizado: {lastUpdate.toLocaleTimeString('pt-BR')}
            </span>
          )}
          <button className="op-dashboard-filter-btn" onClick={togglePause} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {paused ? <><Play size={14} /> Retomar</> : <><Pause size={14} /> Pausar</>}
          </button>
          <button className="op-dashboard-filter-btn" onClick={fetchData} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Atualizar
          </button>
          {!paused && <span className="op-dashboard-realtime-badge">Atualizando a cada 15s</span>}
          {paused && <span className="op-dashboard-realtime-badge" style={{ background: 'rgba(234,179,8,0.15)', color: '#facc15' }}>Pausado</span>}
        </div>
      </div>

      {/* Resumo do Dia */}
      <div className="op-dashboard-cards" style={{ marginBottom: 24 }}>
        <div className="op-dashboard-card blue">
          <div className="op-dashboard-card-icon"><Package size={20} /></div>
          <div className="op-dashboard-card-value">{resumo.paletes_hoje}</div>
          <div className="op-dashboard-card-label">Paletes Hoje</div>
        </div>
        <div className="op-dashboard-card green">
          <div className="op-dashboard-card-icon"><CheckCircle size={20} /></div>
          <div className="op-dashboard-card-value">{resumo.concluidos_hoje}</div>
          <div className="op-dashboard-card-label">Concluídos</div>
        </div>
        <div className="op-dashboard-card orange">
          <div className="op-dashboard-card-icon"><Activity size={20} /></div>
          <div className="op-dashboard-card-value">{resumo.em_andamento_hoje}</div>
          <div className="op-dashboard-card-label">Em Andamento</div>
        </div>
        <div className="op-dashboard-card cyan">
          <div className="op-dashboard-card-icon"><Tag size={20} /></div>
          <div className="op-dashboard-card-value">{(resumo.etiquetas_hoje || 0).toLocaleString('pt-BR')}</div>
          <div className="op-dashboard-card-label">Etiquetas Hoje</div>
        </div>
      </div>

      {/* Paletes em Andamento */}
      <h2 className="op-dashboard-section-title">Paletes em Andamento</h2>
      {paletes.length > 0 ? (
        <div className="op-dashboard-realtime-grid">
          {paletes.map((p) => (
            <div key={p.id} className="op-dashboard-realtime-card">
              <div className="op-dashboard-realtime-card-header">
                <span className="op-dashboard-realtime-card-title">
                  Palete #{p.numero_palete}
                </span>
                <span className={`op-dashboard-badge ${statusColor(p.status)}`}>
                  {statusLabel(p.status)}
                </span>
              </div>
              <div className="op-dashboard-realtime-card-info">
                <span>Cliente: <strong>{p.cliente_nome}</strong></span>
                <span>Conferente: {p.conferente_nome}</span>
              </div>
              <div className="op-dashboard-realtime-card-info">
                <span>Região: {p.area_regiao || '-'}</span>
                <span>Área: {p.area_codigo ? `${p.area_codigo} (${p.area_nome})` : (p.area_nome || '-')}</span>
              </div>
              {p.doca && (
                <div className="op-dashboard-realtime-card-info">
                  <span>Doca: {p.doca}</span>
                </div>
              )}
              <div className="op-dashboard-realtime-card-meta" style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>
                  <Tag size={12} style={{ marginRight: 4 }} />
                  {p.total_etiquetas} etiqueta(s)
                </span>
                <span>
                  <Package size={12} style={{ marginRight: 4 }} />
                  {p.total_pedidos} pedido(s)
                </span>
                {p.total_duplicadas > 0 && (
                  <span style={{ color: '#ef4444' }}>
                    {p.total_duplicadas} duplicada(s)
                  </span>
                )}
              </div>
              <div className="op-dashboard-realtime-card-meta" style={{ fontSize: 11, color: '#64748b' }}>
                Op. #{p.numero_operacao}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="op-dashboard-empty">Nenhum palete em andamento no momento</div>
      )}

      {/* Conferentes Ativos */}
      <h2 className="op-dashboard-section-title">
        <Users size={18} /> Conferentes Ativos (últimos 30 min)
      </h2>
      {conferentes.length > 0 ? (
        <div className="op-dashboard-card-section">
          <table className="op-dashboard-table">
            <thead>
              <tr>
                <th>Conferente</th>
                <th>Leituras (30 min)</th>
                <th>Última Leitura</th>
              </tr>
            </thead>
            <tbody>
              {conferentes.map((c) => (
                <tr key={c.conferente_id}>
                  <td style={{ fontWeight: 600 }}>
                    <span className="op-dashboard-active-dot" />
                    {c.conferente_nome}
                  </td>
                  <td>{c.leituras_ultimos_30min}</td>
                  <td>{new Date(c.ultima_leitura).toLocaleTimeString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="op-dashboard-empty">Nenhum conferente ativo no momento</div>
      )}
    </>
  );
};

export default OperationalDashboardRealtime;
